"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findRealtimeMetricTransferItem = exports.realtimeMetricTransferSummary = exports.realtimeMetricTransferInventory = void 0;
const effect_1 = require("../master-data/effect");
const legacy_metric_inventory_1 = require("./legacy-metric-inventory");
const global_formula_catalog_1 = require("./global-formula-catalog");
const metric_catalog_1 = require("./metric-catalog");
const strictCurrentEvidence = {
    requiresSourceRecordId: true,
    requiresObservedAt: true,
    requiresEffectiveAt: false,
    requiresFiscalYear: false,
};
const strictEventEvidence = {
    ...strictCurrentEvidence,
    requiresEffectiveAt: true,
};
/** A historical observation must identify both its capture and as-of dates. */
const strictHistoricalObservationEvidence = {
    ...strictEventEvidence,
    requiresFiscalYear: false,
};
const strictReportedPeriodEvidence = {
    ...strictEventEvidence,
    requiresFiscalYear: true,
};
const deferredReasons = new Map([
    ['hasOnSiteRequirements', 'Manual-domain field without a current source policy'],
    ['recentFunding', 'Manual-domain field without a current source policy'],
    ['percentEmployeesWithMoreThan10YearsExperience', 'Manual-domain field without a current source policy'],
    ['moveInDateCurrentOffice', 'Pipedrive/manual office data is not a global source'],
    ['moveDateOrLeaseExpirationDate', 'Pipedrive/manual office data is not a global source'],
    ['moveDateOrLeaseExpirationDateIsWithinOneYear', 'Pipedrive/manual office data is not a global source'],
    ['moveDateOrLeaseExpirationDateHasPassed', 'Pipedrive/manual office data is not a global source'],
    ['isMoreThan5YearsSinceMoveInDateCurrentOffice', 'Pipedrive/manual office data is not a global source'],
    ['workspaceSizeInM2', 'Pipedrive/manual office data is not a global source'],
    ['workspaceSizeInM2PerEmployee', 'Pipedrive/manual office data is not a global source'],
    ['lookingForOffice', 'Pipedrive/manual office data is not a global source'],
    ['leaseExpirationDate', 'Pipedrive/manual office data is not a global source'],
    ['leaseExpirationDateIsWithinOneYear', 'Pipedrive/manual office data is not a global source'],
    ['mlLikelyToMoveProbability', 'The ML source lacks current model-version evidence'],
    ['mlIsLikelyToMove', 'The ML source lacks current model-version evidence'],
]);
const activeSocialMetricKeys = new Set([
    'snapchatHandle',
    'linkedinIsVerified',
    'linkedinId',
    'linkedinDomain',
    'linkedinIndustry',
    'linkedinNumberOfEmployees',
    'linkedinAllEmployeeGrowth3Months',
    'linkedinAllEmployeeGrowth6Months',
    'linkedinAllEmployeeGrowth1Year',
    'linkedinAllEmployeeGrowth2Years',
]);
const inScopeSectionPolicy = (section) => {
    switch (section) {
        case 'Company Information':
            return { sourceFamily: 'company_registry', owner: 'core_data', evidence: strictCurrentEvidence };
        case 'Financial Information':
            return { sourceFamily: 'financial_reporting', owner: 'financial_data', evidence: strictReportedPeriodEvidence };
        case 'Financial Metrics':
            return { sourceFamily: 'global_formula', owner: 'financial_data', evidence: strictReportedPeriodEvidence };
        case 'Digital Presence':
        case 'Digital Analytics':
        case 'Digital Tools':
        case 'Website Performance':
        case 'Accessibility':
        case 'Privacy':
        case 'Digital Infrastructure':
            return { sourceFamily: 'domain_crawl', owner: 'web_data', evidence: strictCurrentEvidence };
        case 'Company Buzz':
            return { sourceFamily: 'buzz', owner: 'market_intelligence', evidence: strictEventEvidence };
        case 'Trademarks':
            return { sourceFamily: 'prv', owner: 'market_intelligence', evidence: strictEventEvidence };
        case 'Apollo Data':
        case 'Apollo Headcount':
            return { sourceFamily: 'apollo', owner: 'enrichment', evidence: strictCurrentEvidence };
        case 'Company Group':
            return {
                sourceFamily: 'valu8_group_structure',
                owner: 'core_data',
                evidence: strictCurrentEvidence,
            };
        default:
            return undefined;
    }
};
const deferredSectionReason = (section) => {
    switch (section) {
        case '6sense Data':
            return 'Manual snapshot without source health or observation timestamps';
        case 'NorNorm Data':
            return 'Stale source without an active ingestion pipeline';
        default:
            return undefined;
    }
};
const sourceContractBySystem = {
    company_master: { sourceFamily: 'company_registry', owner: 'core_data' },
    valu8_companies: { sourceFamily: 'company_registry', owner: 'core_data' },
    valu8_company_employees: { sourceFamily: 'workforce', owner: 'core_data' },
    valu8_company_financials: { sourceFamily: 'financial_reporting', owner: 'financial_data' },
    annual_report: { sourceFamily: 'financial_reporting', owner: 'financial_data' },
    buzz: { sourceFamily: 'buzz', owner: 'market_intelligence' },
    company_workplace: { sourceFamily: 'company_workplace', owner: 'core_data' },
    jobtech_active_ads: { sourceFamily: 'jobtech', owner: 'market_intelligence' },
    career_hiring: { sourceFamily: 'career_hiring', owner: 'market_intelligence' },
    domains: { sourceFamily: 'domain_crawl', owner: 'web_data' },
    domain_orgnr_map: { sourceFamily: 'domain_mapping', owner: 'web_data' },
    prv: { sourceFamily: 'prv', owner: 'market_intelligence' },
    apollo: { sourceFamily: 'apollo', owner: 'enrichment' },
    linkedin: { sourceFamily: 'linkedin', owner: 'enrichment' },
    google_places: { sourceFamily: 'google_places', owner: 'workplace_data' },
    valu8_group_structure: { sourceFamily: 'valu8_group_structure', owner: 'core_data' },
};
/**
 * A formula has one accountable owner even when it consumes several facts.
 * This is deliberately explicit: incident routing must not accidentally infer
 * that every KPI is owned by Finance merely because some include revenue.
 */
const globalFormulaOwnerByKey = {
    'workforce.employees.growth_1y': 'core_data',
    'financial.revenue.growth_1y': 'financial_data',
    'financial.revenue.per_employee': 'financial_data',
    'workforce.employees.per_active_workplace': 'core_data',
    'hiring.active_jobposts.per_employee': 'market_intelligence',
    'financial.gross_profit_margin.current': 'financial_data',
    'financial.ebit_margin.current': 'financial_data',
    'financial.ebitda_margin.current': 'financial_data',
    'financial.pre_tax_profit_margin.current': 'financial_data',
    'financial.net_profit_margin.current': 'financial_data',
    'financial.equity_ratio.current': 'financial_data',
    'financial.current_ratio.current': 'financial_data',
    'financial.net_debt_to_ebit.current': 'financial_data',
    'financial.net_debt_to_ebitda.current': 'financial_data',
    'financial.net_debt_to_equity.current': 'financial_data',
    'financial.enterprise_value_to_ebit.current': 'financial_data',
    'financial.enterprise_value_to_ebitda.current': 'financial_data',
    'financial.enterprise_value_to_revenue.current': 'financial_data',
    ...Object.fromEntries(metric_catalog_1.realtimeLinkedInEmployeeGrowthMetrics.map(({ key }) => [key, 'enrichment'])),
};
/**
 * Formula provenance contains one evidence object per input. These requirements
 * therefore describe the temporal fields guaranteed by every input fact, rather
 * than promoting a financial period date from just one side of a mixed formula.
 */
const inputTemporalEvidenceRequirements = (metric) => {
    const globalFormula = (0, global_formula_catalog_1.findGlobalFormula)(metric);
    if (globalFormula !== undefined) {
        const inputRequirements = globalFormula.inputMetrics.map(inputTemporalEvidenceRequirements);
        return {
            requiresEffectiveAt: inputRequirements.every((requirements) => requirements.requiresEffectiveAt),
            requiresFiscalYear: inputRequirements.every((requirements) => requirements.requiresFiscalYear),
        };
    }
    if (metric.startsWith('financial.')) {
        return { requiresEffectiveAt: true, requiresFiscalYear: true };
    }
    if (metric === 'workforce.employees.current') {
        return { requiresEffectiveAt: false, requiresFiscalYear: true };
    }
    if (metric === 'linkedin.profile.employee_count') {
        return { requiresEffectiveAt: false, requiresFiscalYear: false };
    }
    if (metric === 'company.workplaces.active_count' || metric === 'hiring.active_jobposts.current') {
        return { requiresEffectiveAt: false, requiresFiscalYear: false };
    }
    throw new Error(`No temporal evidence contract exists for global formula input ${metric}`);
};
const evidenceRequirementsForGlobalFormula = (metric) => ({
    ...strictCurrentEvidence,
    ...inputTemporalEvidenceRequirements(metric),
});
const companyMetricIndexNames = {
    'linkedin.identity.mapping_verified': ['orgnr_linkedin_id_map_orgnr_idx'],
    'company.registry.keywords.current': ['valu8_companies_keywords_idx'],
    'company.identity.orgnr': ['company_master_realtime_country_prefix_idx'],
    'company.identity.name': ['company_master_realtime_company_name_trgm_idx'],
    'company.identity.previous_names': ['company_master_realtime_previous_names_trgm_idx'],
    'company.location.address': ['company_master_realtime_address_trgm_idx'],
    'company.contact.website': ['company_master_realtime_website_trgm_idx'],
    'company.contact.phone': ['company_master_realtime_phone_idx'],
    'company.contact.email': ['company_master_realtime_email_idx'],
    'company.profile.description_en': ['company_master_realtime_description_en_trgm_idx'],
    'company.industry.sni_code': ['company_master_realtime_sni_prefix_idx'],
    'company.industry.sni_prefix': ['company_master_realtime_sni_prefix_idx'],
    'company.industry.sni_classification': ['company_master_realtime_sni_classification_trgm_idx'],
    'company.operational_status': ['company_master_realtime_operational_status_idx'],
    'company.legal_entity_type': ['company_master_realtime_legal_entity_type_idx'],
    'company.category': ['company_master_realtime_company_category_idx'],
    'company.structure': ['company_master_realtime_company_structure_idx'],
    'company.legal_form': ['company_master_realtime_legal_form_idx'],
    'company.listing_type': ['company_master_realtime_listing_type_idx'],
    'company.registration_date': ['company_master_realtime_registration_date_idx'],
    'company.ownership.has_private_equity_owner': ['company_master_realtime_private_equity_owner_idx'],
    'company.ownership.private_equity_owner': ['company_master_realtime_private_equity_owner_name_trgm_idx'],
    'company.ownership.has_foreign_ultimate_parent': ['company_master_realtime_foreign_ultimate_parent_idx'],
    'company.ownership.owner_count': ['company_master_realtime_owner_count_idx'],
    'company.ownership.ultimate_parent_company_name': ['company_master_realtime_ultimate_parent_company_trgm_idx'],
    'company.ownership.ultimate_parent_orgnr': ['company_master_realtime_ultimate_parent_orgnr_idx'],
    'company.ownership.parent_company_name': [
        'valu8_group_structure_relation_child_idx',
        'valu8_group_structure_company_company_idx',
    ],
    'company.leadership.ceo_name': ['company_master_realtime_ceo_name_trgm_idx'],
    'company.leadership.ceo_birth_year': ['company_master_realtime_ceo_birth_year_idx'],
    'company.leadership.chairman_name': ['company_master_realtime_chairman_name_trgm_idx'],
    'company.leadership.signatory': ['company_master_realtime_signatory_trgm_idx'],
    'company.leadership.chairman_birth_year': ['company_master_realtime_chairman_birth_year_idx'],
    'company.capital.has_share_issues': ['company_master_realtime_share_issues_idx'],
    'company.capital.share_count': ['company_master_realtime_share_count_idx'],
    'company.location.postal_code': ['company_master_realtime_postal_code_idx'],
    'company.location.municipality': ['company_master_realtime_municipality_idx'],
    'company.location.county': ['company_master_realtime_county_idx'],
    'company.location.region': ['company_master_realtime_region_idx'],
    'company.country.iso3166': ['company_master_realtime_country_prefix_idx'],
    'company.location.city': ['company_master_realtime_city_trgm_idx'],
};
const financialMetricIndexNames = (metric) => {
    if ([
        'financial.revenue.current',
        'financial.ebit.current',
        'financial.ebitda.current',
        'financial.net_profit.current',
    ].includes(metric)) {
        return ['valu8_company_financials_realtime_metrics_idx', 'annual_report_realtime_orgnr_fiscal_year_metrics_idx'];
    }
    return metric.startsWith('financial.') && metric.endsWith('.current')
        ? ['valu8_company_financials_realtime_statement_metrics_v3_idx', 'annual_report_realtime_statement_metrics_v3_idx']
        : undefined;
};
const uniqueIndexNames = (indexNames) => [
    ...new Set(indexNames.flat()),
];
const realtimeBuzzEventMetricByKey = new Map(metric_catalog_1.realtimeBuzzEventMetrics.map((metric) => [metric.key, metric]));
const realtimePrvTrademarkMetricByKey = new Map(metric_catalog_1.realtimePrvTrademarkMetrics.map((metric) => [metric.key, metric]));
const realtimeApolloOrganizationMetricByKey = new Map(metric_catalog_1.realtimeApolloOrganizationMetrics.map((metric) => [metric.key, metric]));
const realtimeApolloHeadcountCurrentMetricByKey = new Map(metric_catalog_1.realtimeApolloHeadcountCurrentMetrics.map((metric) => [metric.key, metric]));
const realtimeApolloHeadcountHistoricalMetricByKey = new Map(metric_catalog_1.realtimeApolloHeadcountHistoricalMetrics.map((metric) => [metric.key, metric]));
const realtimeApolloHeadcountGrowthMetricByKey = new Map(metric_catalog_1.realtimeApolloHeadcountGrowthMetrics.map((metric) => [metric.key, metric]));
const realtimeLinkedInCurrentMetricByKey = new Map(metric_catalog_1.realtimeLinkedInCurrentMetrics.map((metric) => [metric.key, metric]));
const realtimeLinkedInEmployeeGrowthMetricByKey = new Map(metric_catalog_1.realtimeLinkedInEmployeeGrowthMetrics.map((metric) => [metric.key, metric]));
const realtimeGooglePlacesCurrentMetricByKey = new Map(metric_catalog_1.realtimeGooglePlacesCurrentMetrics.map((metric) => [metric.key, metric]));
const requiredIndexNamesForMetric = (metric) => {
    if (metric_catalog_1.realtimeGroupParentMetrics.some(({ key }) => key === metric)) {
        return ['valu8_group_structure_relation_child_idx', 'valu8_group_structure_company_company_idx'];
    }
    if (metric_catalog_1.realtimeGroupMetrics.some(({ key }) => key === metric)) {
        return [
            'valu8_group_structure_company_company_idx',
            'valu8_group_metric_history_ultimate_parent_idx',
            'valu8_group_metric_history_sys_period_idx',
        ];
    }
    if (realtimeLinkedInEmployeeGrowthMetricByKey.has(metric)) {
        return [
            'linkedin_company_current_realtime_orgnr_observed_at_idx',
            'linkedin_company_observation_identity_observed_idx',
            'linkedin_company_observation_orgnr_observed_idx',
        ];
    }
    if (metric.startsWith('hiring.careers.open_ads.')) {
        return ['career_company_hiring_metrics_exact_idx', 'career_page_domain_idx', 'domain_orgnr_map_realtime_orgnr_idx'];
    }
    const globalFormula = (0, global_formula_catalog_1.findGlobalFormula)(metric);
    if (globalFormula !== undefined) {
        return uniqueIndexNames(globalFormula.inputMetrics.map(requiredIndexNamesForMetric));
    }
    const companyIndexNames = companyMetricIndexNames[metric];
    if (companyIndexNames !== undefined)
        return companyIndexNames;
    const financialIndexNames = financialMetricIndexNames(metric);
    if (financialIndexNames !== undefined)
        return financialIndexNames;
    const buzzEventMetric = realtimeBuzzEventMetricByKey.get(metric);
    if (buzzEventMetric !== undefined) {
        return [buzzEventMetric.sourceIndexName, 'buzz_orgnr_map_realtime_orgnr_buzz_id_idx'];
    }
    if (realtimePrvTrademarkMetricByKey.has(metric)) {
        const sourceIndexes = metric.startsWith('trademark.applicant_mapping.')
            ? ['prv_trademark_applicant_map_realtime_orgnr_application_idx']
            : ['prv_trademark_applicant_map_realtime_orgnr_application_idx', 'prv_trademark_realtime_application_date_idx'];
        return metric === 'trademark.nice_class_count' || metric === 'trademark.applicant.nice_classes'
            ? [...sourceIndexes, 'prv_trademark_goods_service_realtime_application_class_idx']
            : sourceIndexes;
    }
    if (realtimeApolloOrganizationMetricByKey.has(metric) || realtimeApolloHeadcountCurrentMetricByKey.has(metric)) {
        return ['domain_orgnr_map_realtime_orgnr_idx', 'idx_apollo_org_domain_country'];
    }
    if (realtimeApolloHeadcountHistoricalMetricByKey.has(metric)) {
        return [
            'apollo_orgnr_obs_realtime_orgnr_observed_at_idx',
            'idx_apollo_organization_history_id',
            'idx_apollo_organization_history_sys_period',
        ];
    }
    if (realtimeLinkedInCurrentMetricByKey.has(metric)) {
        return ['linkedin_company_current_realtime_orgnr_observed_at_idx'];
    }
    if (realtimeGooglePlacesCurrentMetricByKey.has(metric)) {
        return ['google_place_realtime_orgnr_observed_at_idx'];
    }
    if (metric === 'web.domain.mapping_verified') {
        return ['domain_orgnr_map_realtime_verified_observation_idx'];
    }
    switch (metric) {
        case 'workforce.employees.current':
        case 'workforce.employees.current_source':
            return ['valu8_company_employees_realtime_current_idx'];
        case 'company.workplaces.active_count':
            return ['company_workplace_orgnr_status_idx'];
        case 'hiring.active_jobposts.current':
            return ['jobtech_active_ad_realtime_company_validity_idx', 'jobtech_active_ad_realtime_company_history_idx'];
        case 'signal.buzz.article_count.within_14d_mapped':
        case 'signal.changed_ceo.within_14d_mapped':
            return ['buzz_orgnr_map_realtime_orgnr_buzz_id_idx'];
        default:
            if (metric.startsWith('web.'))
                return ['domain_orgnr_map_realtime_orgnr_idx'];
            throw new Error(`No source-index contract exists for authorable metric ${metric}`);
    }
};
const authorableMetricPolicy = (v1Metric) => {
    const metric = (0, metric_catalog_1.findRealtimeMetric)(v1Metric);
    if (metric === undefined || !(0, metric_catalog_1.isAuthorableRealtimeMetric)(metric))
        return undefined;
    const evidencePolicy = metric.evidencePolicy;
    if (evidencePolicy === undefined)
        return undefined;
    if (realtimeApolloHeadcountGrowthMetricByKey.has(v1Metric)) {
        return {
            sourceFamily: 'apollo',
            owner: 'enrichment',
            evidence: strictHistoricalObservationEvidence,
            requiredIndexNames: requiredIndexNamesForMetric(v1Metric),
        };
    }
    if (realtimeLinkedInEmployeeGrowthMetricByKey.has(v1Metric)) {
        return {
            sourceFamily: 'linkedin',
            owner: 'enrichment',
            evidence: strictHistoricalObservationEvidence,
            requiredIndexNames: requiredIndexNamesForMetric(v1Metric),
        };
    }
    if ((0, global_formula_catalog_1.findGlobalFormula)(v1Metric) !== undefined) {
        const owner = globalFormulaOwnerByKey[v1Metric];
        if (owner === undefined)
            throw new Error(`No owner contract exists for global formula ${v1Metric}`);
        return {
            sourceFamily: 'global_formula',
            owner,
            evidence: evidenceRequirementsForGlobalFormula(v1Metric),
            requiredIndexNames: requiredIndexNamesForMetric(v1Metric),
        };
    }
    const sourceContract = sourceContractBySystem[evidencePolicy.sourceSystems[0]];
    if (sourceContract === undefined) {
        throw new Error(`No source-family contract exists for authorable metric ${v1Metric}`);
    }
    return {
        ...sourceContract,
        evidence: v1Metric === 'workforce.employees.current_source'
            ? { ...strictCurrentEvidence, requiresFiscalYear: true }
            : evidencePolicy.temporalSemantics === 'published_event'
                ? strictEventEvidence
                : evidencePolicy.temporalSemantics === 'historical_observation'
                    ? strictHistoricalObservationEvidence
                    : evidencePolicy.temporalSemantics === 'reported_period'
                        ? strictReportedPeriodEvidence
                        : strictCurrentEvidence,
        requiredIndexNames: requiredIndexNamesForMetric(v1Metric),
    };
};
const deferredItem = (legacyKey, label, section, deferReason) => ({
    legacyKey,
    label,
    section,
    lifecycle: 'deferred',
    sourceFamily: 'deferred',
    owner: 'deferred',
    evidence: undefined,
    v1Metric: undefined,
    requiredIndexNames: [],
    requiredIndexStatus: 'not_applicable',
    compilerCoverageTest: undefined,
    migrationTestStatus: 'not_applicable',
    deferReason,
});
const plannedItem = (legacyKey, label, section, policy) => ({
    legacyKey,
    label,
    section,
    lifecycle: 'planned',
    ...policy,
    v1Metric: undefined,
    requiredIndexNames: [],
    requiredIndexStatus: 'required_before_authorable',
    compilerCoverageTest: undefined,
    migrationTestStatus: 'pending',
    deferReason: undefined,
});
const authorableItem = (legacyKey, label, section, policy, v1Metric, compilerCoverageTest = 'apps/bloodline-api/src/scoring/realtime-source-indexes.spec.ts') => ({
    legacyKey,
    label,
    section,
    lifecycle: 'authorable',
    ...policy,
    v1Metric,
    requiredIndexStatus: 'covered',
    compilerCoverageTest,
    migrationTestStatus: 'covered',
    deferReason: undefined,
});
const authorableCrosswalkMetric = (legacyKey) => {
    const crosswalk = legacy_metric_inventory_1.legacyMetricMigrationCrosswalk.find((item) => item.legacyKey === legacyKey);
    if (crosswalk === undefined || crosswalk.disposition === 'planned')
        return undefined;
    const policy = authorableMetricPolicy(crosswalk.v1Metric);
    return policy === undefined ? undefined : { v1Metric: crosswalk.v1Metric, policy };
};
/**
 * Complete deterministic transfer contract for every current legacy field.
 * An unfamiliar field or section intentionally throws so a source-schema
 * expansion cannot silently bypass this migration inventory.
 */
const realtimeMetricTransferInventory = () => effect_1.effectDataFields.map((field) => {
    const directDeferReason = deferredReasons.get(field.key);
    if (directDeferReason !== undefined) {
        return deferredItem(field.key, field.title, field.section, directDeferReason);
    }
    const sectionDeferReason = deferredSectionReason(field.section);
    if (sectionDeferReason !== undefined) {
        return deferredItem(field.key, field.title, field.section, sectionDeferReason);
    }
    if (field.section === 'Company Metrics' && field.key === 'annualReportEmployeeGrowth') {
        return plannedItem(field.key, field.title, field.section, {
            sourceFamily: 'financial_reporting',
            owner: 'financial_data',
            evidence: strictReportedPeriodEvidence,
        });
    }
    if (activeSocialMetricKeys.has(field.key)) {
        const mappedMetric = authorableCrosswalkMetric(field.key);
        if (mappedMetric !== undefined) {
            return authorableItem(field.key, field.title, field.section, mappedMetric.policy, mappedMetric.v1Metric);
        }
        return plannedItem(field.key, field.title, field.section, {
            sourceFamily: field.key === 'snapchatHandle' ? 'domain_crawl' : 'linkedin',
            owner: field.key === 'snapchatHandle' ? 'web_data' : 'enrichment',
            evidence: strictCurrentEvidence,
        });
    }
    if (field.key === 'isSittingOnCoWorkingSpace') {
        return plannedItem(field.key, field.title, field.section, {
            sourceFamily: 'crowdsourced_coworking',
            owner: 'workplace_data',
            evidence: strictCurrentEvidence,
        });
    }
    if (field.key === 'prvApplicantNiceClasses' || field.key === 'prvApplicantTopMarks') {
        const v1Metric = field.key === 'prvApplicantNiceClasses' ? 'trademark.applicant.nice_classes' : 'trademark.applicant.top_marks';
        const policy = authorableMetricPolicy(v1Metric);
        if (policy === undefined) {
            throw new Error(`PRV metric ${v1Metric} has no evidence/index contract`);
        }
        return authorableItem(field.key, field.title, field.section, policy, v1Metric);
    }
    const policy = inScopeSectionPolicy(field.section);
    if (policy !== undefined) {
        const mappedMetric = authorableCrosswalkMetric(field.key);
        return mappedMetric === undefined
            ? plannedItem(field.key, field.title, field.section, policy)
            : authorableItem(field.key, field.title, field.section, mappedMetric.policy, mappedMetric.v1Metric, field.key === 'companyKeywords' || field.key === 'numberOfEmployeesSource'
                ? 'apps/bloodline-api/src/scoring/realtime-query-builder.integration.spec.ts'
                : undefined);
    }
    throw new Error(`No realtime metric transfer policy exists for ${field.key} in ${field.section}`);
});
exports.realtimeMetricTransferInventory = realtimeMetricTransferInventory;
const realtimeMetricTransferSummary = () => (0, exports.realtimeMetricTransferInventory)().reduce((summary, item) => ({ ...summary, [item.lifecycle]: summary[item.lifecycle] + 1 }), { authorable: 0, planned: 0, deferred: 0 });
exports.realtimeMetricTransferSummary = realtimeMetricTransferSummary;
const findRealtimeMetricTransferItem = (legacyKey) => (0, exports.realtimeMetricTransferInventory)().find((item) => item.legacyKey === legacyKey);
exports.findRealtimeMetricTransferItem = findRealtimeMetricTransferItem;
//# sourceMappingURL=realtime-metric-transfer-inventory.js.map